import { useMemo } from 'react';
import { match, P } from 'ts-pattern';
import { Gwei } from '../lib/gwei';
import { calcPnl, calcPnlPercentage } from '../lib/positionHelpers';
import { useProductPricesSub } from './useProductPricesSub';

export const useSlTpPnl = ({
  stopLoss,
  takeProfit,
  avgPrice,
  side,
  amount,
}: {
  stopLoss: bigint | undefined;
  takeProfit: bigint | undefined;
  avgPrice: bigint | undefined;
  side: 'long' | 'short';
  amount: bigint | undefined;
}) => {
  const { stopLossPnl, stopLossPnlPercentage } = useMemo(() => {
    if (!stopLoss || !avgPrice || !amount) return {};
    const size = side === 'long' ? amount : Gwei.flipSide(amount);
    const pnlPercentage = calcPnlPercentage(stopLoss, avgPrice, size);
    return {
      stopLossPnl: calcPnl(stopLoss, avgPrice, size),
      // Invert PnL percentage sign for stop loss display:
      // For long positions: stop loss below entry price results in negative PnL, but we display it as positive
      // For short positions: stop loss above entry price results in negative PnL, but we display it as positive
      stopLossPnlPercentage: -pnlPercentage,
    };
  }, [stopLoss, avgPrice, amount, side]);

  const { takeProfitPnl, takeProfitPnlPercentage } = useMemo(() => {
    if (!takeProfit || !avgPrice || !amount) return {};
    const size = side === 'long' ? amount : Gwei.flipSide(amount);
    return {
      takeProfitPnl: calcPnl(takeProfit, avgPrice, size),
      takeProfitPnlPercentage: calcPnlPercentage(takeProfit, avgPrice, size),
    };
  }, [takeProfit, avgPrice, amount, side]);

  return {
    takeProfitPnl,
    takeProfitPnlPercentage,
    stopLossPnlPercentage,
    stopLossPnl,
  };
};

export const useTpSlError = ({
  takeProfitPrice,
  stopLossPrice,
  oraclePrice,
  side,
}: {
  takeProfitPrice: bigint | undefined;
  stopLossPrice: bigint | undefined;
  oraclePrice: bigint | undefined;
  // This is the side or the related order/ or position
  side: 'long' | 'short';
}) => {
  // Handle missing oracle price
  if (!oraclePrice) {
    return {
      takeProfitError: 'Oracle price is not available',
      stopLossError: 'Oracle price is not available',
    };
  }

  // Validate take-profit price
  const takeProfitError = match({ price: takeProfitPrice, side })
    .with({ price: undefined }, () => null)
    .with({ price: 0n }, () => 'Take profit price cannot be zero')
    .with(
      { side: 'long', price: P.when((p) => p && p <= oraclePrice) },
      () => 'Take profit price must be above oracle price for long positions'
    )
    .with(
      { side: 'short', price: P.when((p) => p && p >= oraclePrice) },
      () => 'Take profit price must be below oracle price for short positions'
    )
    .otherwise(() => null);

  // Validate stop-loss price
  const stopLossError = match({ price: stopLossPrice, side })
    .with({ price: undefined }, () => null)
    .with({ price: 0n }, () => 'Stop loss price cannot be zero')
    .with(
      { side: 'long', price: P.when((p) => p && p >= oraclePrice) },
      () => 'Stop loss price must be below oracle price for long positions'
    )
    .with(
      { side: 'short', price: P.when((p) => p && p <= oraclePrice) },
      () => 'Stop loss price must be above oracle price for short positions'
    )
    .otherwise(() => null);

  return { takeProfitError, stopLossError };
};

export const useHasSlTpError = (params: {
  takeProfitPrice: bigint | undefined;
  stopLossPrice: bigint | undefined;
  productId: string | undefined;
  // This is the side or the related order/ or position
  side: 'long' | 'short';
}) => {
  const { data: pricesByProductId } = useProductPricesSub();
  const oraclePrice = params.productId
    ? pricesByProductId?.[params.productId]?.oraclePrice
    : undefined;
  const { takeProfitError, stopLossError } = useTpSlError({ ...params, oraclePrice });
  return useMemo(() => {
    let hasTakeProfitError = false;
    let hasStopLossError = false;
    if (takeProfitError) {
      hasTakeProfitError = true;
    }
    if (stopLossError) {
      hasStopLossError = true;
    }

    return {
      hasTakeProfitError,
      hasStopLossError,
    };
  }, [takeProfitError, stopLossError]);
};
